/**
 * Game sizes (R-SIZE-*): small 6/2, medium 12/4 and large 28/6 ships/forts a side. Each size is
 * one declarative record — map, fleet and port quotas — and the engine reads it like any other
 * ruleset: `fleet` and `deployment` go into the ruleset overrides, the map is passed as is.
 * Nothing else in the engine knows about sizes.
 */
import { LARGE_MAP, MEDIUM_MAP, SMALL_MAP } from './maps.ts';
import type { GameSetup } from './queries.ts';
import { CLASSIC_DEPLOYMENT, CLASSIC_FLEET } from './ruleset.ts';
import type { Deployment, Fleet, MapDef, Player, Ruleset, Side } from './types.ts';

export const GAME_SIZE_IDS = ['small', 'medium', 'large'] as const;
export type GameSizeId = (typeof GAME_SIZE_IDS)[number];

export const DEFAULT_GAME_SIZE: GameSizeId = 'medium';

export interface GameSizeConfig {
  readonly id: GameSizeId;
  /** Developer-facing; the UI names sizes by id. */
  readonly name: string;
  readonly shipsPerSide: number;
  readonly fortsPerSide: { readonly firstLine: number; readonly secondLine: number };
  readonly royalPortsPerSide: 1;
  readonly map: MapDef;
  /** The same for both sides; players cannot change it. */
  readonly fleet: Fleet;
  readonly deployment: Deployment;
}

export const GAME_SIZES: Readonly<Record<GameSizeId, GameSizeConfig>> = {
  small: {
    id: 'small',
    name: 'Small',
    shipsPerSide: 6,
    fortsPerSide: { firstLine: 1, secondLine: 1 },
    royalPortsPerSide: 1,
    map: SMALL_MAP,
    fleet: {
      ship_of_line: 1,
      sail_frigate: 1,
      brig: 1,
      galley: 1,
      steam_frigate: 1,
      monitor: 1,
    },
    deployment: { firstLine: 3, secondLine: 3, largePort: 3 },
  },
  medium: {
    id: 'medium',
    name: 'Medium',
    shipsPerSide: 12,
    fortsPerSide: { firstLine: 2, secondLine: 2 },
    royalPortsPerSide: 1,
    map: MEDIUM_MAP,
    fleet: {
      first_rate: 1,
      ship_of_line: 2,
      sail_frigate: 2,
      brig: 2,
      galley: 1,
      galleon: 1,
      steam_frigate: 1,
      steam_corvette: 1,
      monitor: 1,
    },
    deployment: { firstLine: 3, secondLine: 3, largePort: 3 },
  },
  large: {
    id: 'large',
    name: 'Large',
    shipsPerSide: 28,
    fortsPerSide: { firstLine: 3, secondLine: 3 },
    royalPortsPerSide: 1,
    map: LARGE_MAP,
    fleet: CLASSIC_FLEET,
    deployment: CLASSIC_DEPLOYMENT,
  },
};

export function isGameSizeId(value: unknown): value is GameSizeId {
  return (GAME_SIZE_IDS as readonly unknown[]).includes(value);
}

/** The ruleset overrides a size brings; anything else in `overrides` is kept. */
export function sizeRuleset(size: GameSizeId, overrides: Partial<Ruleset> = {}): Partial<Ruleset> {
  const { fleet, deployment } = GAME_SIZES[size];
  return { ...overrides, fleet, deployment };
}

/** The setup of a new game of the given size: the one way new games are made. */
export function sizedSetup(
  size: GameSizeId,
  seed: number,
  players: Readonly<Record<Side, Player>>,
  overrides: Partial<Ruleset> = {},
): GameSetup {
  return { size, map: GAME_SIZES[size].map, ruleset: sizeRuleset(size, overrides), seed, players };
}

/**
 * The size a setup was made with. Games from before sizes carry none: they are 28/6 games on
 * their own map and stay so (`null`).
 */
export function setupSize(setup: GameSetup): GameSizeId | null {
  return isGameSizeId(setup.size) ? setup.size : null;
}
